import { usePageLayout } from '@app/router/index'
import { ROUTES } from '@app/router/routes'
import { useLanguage } from '@shared/contexts/LanguageContext'
import { useEffect, useState } from 'react'
import { isYouTubeUrl, getYouTubeEmbedUrl, getYouTubeThumbnail } from '@shared/utils/videoUtils'
import ActivityVideosSection from '../components/ActivityVideosSection'
import { getGuardianExpoHomeVideoList } from '../api/guardianExpoService'

export default function GuardianExpoPage() {
  const { t } = useLanguage()
  const { setLayoutOptions } = usePageLayout()
  const [videos, setVideos] = useState([])
  const [selectedVideo, setSelectedVideo] = useState(null)

  useEffect(() => {
    setLayoutOptions({ noPaddingBottom: true })
    return () => setLayoutOptions({})
  }, [setLayoutOptions])

  useEffect(() => {
    getGuardianExpoHomeVideoList()
      .then((res) => setVideos(res.items || []))
      .catch((err) => console.error('Failed to fetch guardian expo home videos:', err))
  }, [])

  return (
    <div style={{ backgroundColor: '#FEFEFE' }}>

      {/* ── Banner ── */}
      <div className="relative w-full">
        <img
          src="/image/guardian-expo/banner_desktop.webp"
          alt={t('guardianExpo.pageTitle')}
          className="w-full h-auto block"
        />
      </div>

      {/* ── Banner 文字（水平置中，同中台心傳樣式） ── */}
      <div className="relative w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto" style={{ zIndex: 1 }}>
        <section className="px-6 pt-10 pb-10 text-center flex flex-col items-center">
          <h2
            className="font-['Noto_Serif_TC']"
            style={{ color: '#171717', fontSize: 24, fontWeight: 500, lineHeight: '36px' }}
          >
            {t('guardianExpo.introSubtitle')}
          </h2>
          <p
            className="font-['Noto_Sans_TC'] whitespace-pre-line"
            style={{ color: '#6E625B', fontSize: 20, fontWeight: 400, lineHeight: '32px', letterSpacing: '0.38px', marginTop: 12, maxWidth: 350 }}
          >
            {t('guardianExpo.introContent')}
          </p>
        </section>
      </div>

      {/* ── 相關活動 ── */}
      <div className="w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto">
        <section className="px-6" style={{ paddingTop: 20 }}>
          <div style={{ borderLeft: '6px solid #BC313D', paddingLeft: 20 }}>
            <h2 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px' }}>
              <span style={{ color: '#171717' }}>{t('guardianExpo.relatedActivitiesTitleBlack')}</span><br />
              <span style={{ color: '#BC313D' }}>{t('guardianExpo.relatedActivitiesTitleRed')}</span>
            </h2>
          </div>

          <div style={{ borderLeft: '1px solid #6E625B', paddingLeft: 26, paddingTop: 32, paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 24 }}>
            {[
              { href: ROUTES.GUARDIAN_EXPO_LECTURE, image: '/image/guardian-expo/card_lecture.webp', label: t('guardianExpo.lecture.title'), desc: t('guardianExpo.lecture.cardDesc'), imageSide: 'left' },
              { href: ROUTES.GUARDIAN_EXPO_MARKET, image: '/image/guardian-expo/card_market.webp', label: t('guardianExpo.market.title'), desc: t('guardianExpo.market.cardDesc'), imageSide: 'right' },
              { href: ROUTES.GUARDIAN_EXPO_CHALLENGE, image: '/image/guardian-expo/card_challenge.webp', label: t('guardianExpo.challenge.title'), desc: t('guardianExpo.challenge.cardDesc'), imageSide: 'left' },
            ].map((item, idx, arr) => (
              <div key={item.href}>
                <a
                  href={item.href}
                  className="flex items-center"
                  style={{ gap: 16, textDecoration: 'none', flexDirection: item.imageSide === 'left' ? 'row' : 'row-reverse' }}
                >
                  <div className="w-[150px] md:w-[40%]" style={{ aspectRatio: '1/1', flexShrink: 0, position: 'relative', background: '#FCF6ED', boxShadow: '8px 7px 10px rgba(110,98,91,0.88)', borderRadius: 10, overflow: 'hidden', border: '6px solid #FFFFFF', outline: '6px solid rgba(110,98,91,0.15)', outlineOffset: '-4px' }}>
                    <img src={item.image} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'cover', }} />
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-center min-w-0">
                    <p className="font-['Noto_Sans_TC']" style={{ alignSelf: 'stretch', color: '#6E625B', fontSize: 20, fontWeight: 400, lineHeight: '32px', letterSpacing: '0.38px' }}>
                      {item.label}
                    </p>
                    <p className="font-['Noto_Sans_TC'] line-clamp-2" style={{ alignSelf: 'stretch', color: '#6E625B', fontSize: 15, fontWeight: 300, lineHeight: '22.5px' }}>
                      {item.desc}
                    </p>
                  </div>
                </a>
                {idx < arr.length - 1 && (
                  <div className="mt-6" style={{ height: 0, borderTop: '1px solid rgba(110,98,91,0.08)' }} />
                )}
              </div>
            ))}
          </div>

          <div className="h-6" style={{ borderLeft: '1px solid #6E625B', paddingLeft: 20 }} />
        </section>
      </div>

      {videos.length > 0 && <ActivityVideosSection
        videos={videos.map((scene) => ({
          id: scene.behind_scenes_id,
          thumbnail: scene.media_url && isYouTubeUrl(scene.media_url) ? getYouTubeThumbnail(scene.media_url) : undefined,
          title: scene.title,
          onClick: () => scene.media_url && setSelectedVideo(scene),
        }))}
        last
      />}

      {/* ── 活動影音 Modal ── */}
      {selectedVideo?.media_url && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <button
            onClick={() => setSelectedVideo(null)}
            className="fixed top-4 right-4 z-[10000] w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full border-2 border-white/30 text-white transition-all"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              {isYouTubeUrl(selectedVideo.media_url) ? (
                <iframe
                  src={`${getYouTubeEmbedUrl(selectedVideo.media_url)}?autoplay=1&rel=0&modestbranding=1`}
                  title={selectedVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              ) : (
                <video
                  src={selectedVideo.media_url}
                  controls
                  autoPlay
                  playsInline
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                  className="absolute inset-0 w-full h-full"
                />
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
